import sortBy from 'lodash/sortBy';

import {
  ACTIVITIES_COMPONENT_TYPE_AUDIO,
  ACTIVITIES_COMPONENT_TYPE_DOCUMENT,
  ACTIVITIES_COMPONENT_TYPE_EBOOK,
  ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH,
  ACTIVITIES_COMPONENT_TYPE_IMAGE,
  ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE,
  ACTIVITIES_COMPONENT_TYPE_INFO_BOX,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE,
  ACTIVITIES_COMPONENT_TYPE_LINK,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_SLIDESHOW,
  ACTIVITIES_COMPONENT_TYPE_VIDEO,
  ACTIVITIES_COMPONENT_TYPE_WEBTOOL,
  ACTIVITIES_COMPONENT_TYPE_VOCABULARY,
  ACTIVITIES_COMPONENT_TYPE_TABLE,
  ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_DRAW,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE,
  ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE,
  ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION,
  ACTIVITIES_COMPONENT_TYPE_GIF,
  ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE,
  LanguageCodes,
} from 'global/constants';
import {
  ComponentAudio,
  ComponentColumn,
  ComponentDocument,
  ComponentEbook,
  ComponentEbookInteractive,
  ComponentGuidedResearch,
  ComponentImage,
  ComponentImageSphere,
  ComponentInfoBox,
  ComponentInteractive,
  ComponentLink,
  ComponentEquationResponse,
  ComponentMultipleChoice,
  ComponentResponse,
  ComponentSlideshow,
  ComponentVideo,
  ComponentVocabulary,
  ComponentTable,
  ComponentDiscussion,
  ComponentDraw,
  ComponentInteractiveResponseCategorise,
  ComponentInteractiveResponseSequence,
  ComponentRiveAnimation,
  ComponentGif,
  ComponentFillInBlanks,
} from '../component.constants';
import { ColumnPrintWrapper } from './columns-print.style';
import VideoPrint from '../video/video-print';
import ResponsePrint from '../response/response-print';
import ImagePrint from '../image/image-print';
import ImageSpherePrint from '../image-sphere/image-sphere-print';
import GuidedResearchPrint from '../guided-research/guided-research-print';
import WebToolPrint from '../webtool/webtool-print';
import LinkPrint from '../link/link-print';
import EquationResponsePrint from '../equation-response/equation-response-print';
import InteractiveEbookPrint from '../interactive-ebook/interactive-ebook-print';
import InteractivePrint from '../interactive/interactive-print';
import DocumentPrint from '../document/document-print';
import EbookPrint from '../ebook/ebook-print';
import MultipleChoicePrint from '../multiple-choice/multiple-choice-print';
import SlideshowPrint from '../slideshow/slideshow-print';
import VocabularyPrint from '../vocabulary/vocabulary-print';
import InfoBoxPrint from '../info-box/info-box-print';
import TablePrint from '../table/table-print';
import DiscussionPrint from '../discussion/discussion-print';
import DrawPrint from '../draw/draw-print';
import InteractiveResponseSequencePrint from '../interactive-response/sequence/interactive-response-sequence-print';
import InteractiveResponseCategorisePrint from '../interactive-response/categorise/interactive-response-categorise-print';
import { ComponentViewWrapper } from '../components-view-wrapper.style';
import { CommonPrintProps } from 'approot/activity-builder/activity-builder.constants';
import RivePrint from '../rive/rive-print';
import GifPrint from '../gif/gif-print';
import FillInBlanksPrint from '../fill-in-blanks/fill-in-blanks-print';
import {
  getAppliedColour,
  isBackgroundApplied,
  isBorderApplied,
} from 'approot/activity-builder/components/shared/component-settings-borders.utils';
import { getStyleProperties } from 'approot/activity-builder/components/components.utils';

type Props = {
  column: ComponentColumn;
  language: LanguageCodes;
  contentVersion: string | null | undefined;
  onRenderComplete?: () => void;
} & CommonPrintProps;

const ColumnsComponentsPrint = ({
  column,
  isPrintPreview,
  language,
  contentVersion,
  onRenderComplete,
}: Props) => {
  const sortedComponents = sortBy(column.components, 'sort');
  return (
    <ColumnPrintWrapper>
      {sortedComponents.map(component => {
        return (
          <ComponentViewWrapper
            key={component.id}
            $colours={getAppliedColour(getStyleProperties(component))}
            $showBorder={isBorderApplied(getStyleProperties(component))}
            $showBackground={isBackgroundApplied(getStyleProperties(component))}
          >
            {component.type === ACTIVITIES_COMPONENT_TYPE_INFO_BOX && (
              <InfoBoxPrint {...(component as ComponentInfoBox)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_SLIDESHOW && (
              <SlideshowPrint {...(component as ComponentSlideshow)} />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_RESPONSE && (
              <ResponsePrint {...(component as ComponentResponse)} />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_MULTIPLE_CHOICE && (
              <MultipleChoicePrint
                {...(component as ComponentMultipleChoice)}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK && (
              <EbookPrint
                {...(component as ComponentEbook)}
                isPrintPreview={isPrintPreview}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_DOCUMENT && (
              <DocumentPrint
                {...(component as ComponentDocument)}
                isPrintPreview={isPrintPreview}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_VIDEO && (
              <VideoPrint
                {...(component as ComponentVideo)}
                isPrintPreview={isPrintPreview}
                contentVersion={contentVersion}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_AUDIO && (
              <VideoPrint
                {...(component as ComponentAudio)}
                isPrintPreview={isPrintPreview}
                contentVersion={contentVersion}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE && (
              <ImagePrint {...(component as ComponentImage)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_IMAGE_SPHERE && (
              <ImageSpherePrint {...(component as ComponentImageSphere)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_INTERACTIVE && (
              <InteractivePrint
                {...(component as ComponentInteractive)}
                isPrintPreview={isPrintPreview}
                contentVersion={contentVersion}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_EBOOK_INTERACTIVE && (
              <InteractiveEbookPrint
                {...(component as ComponentEbookInteractive)}
                isPrintPreview={isPrintPreview}
                contentVersion={contentVersion}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_LINK && (
              <LinkPrint
                {...(component as ComponentLink)}
                isPrintPreview={isPrintPreview}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_WEBTOOL && (
              <WebToolPrint
                {...(component as ComponentLink)}
                isPrintPreview={isPrintPreview}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_GUIDED_RESEARCH && (
              <GuidedResearchPrint
                {...(component as ComponentGuidedResearch)}
                isPrintPreview={isPrintPreview}
                contentVersion={contentVersion}
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_EQUATION_RESPONSE && (
              <EquationResponsePrint
                {...(component as ComponentEquationResponse)}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_VOCABULARY && (
              <VocabularyPrint {...(component as ComponentVocabulary)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_TABLE && (
              <TablePrint {...(component as ComponentTable)} />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_DISCUSSION_RESPONSE && (
              <DiscussionPrint {...(component as ComponentDiscussion)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_DRAW && (
              <DrawPrint
                {...(component as ComponentDraw)}
                language={language}
                onRenderComplete={onRenderComplete}
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE && (
              <InteractiveResponseSequencePrint
                {...(component as ComponentInteractiveResponseSequence)}
                isPrintPreview={isPrintPreview}
              />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_TYPE_INTERACTIVE_RESPONSE_CATEGORISE && (
              <InteractiveResponseCategorisePrint
                {...(component as ComponentInteractiveResponseCategorise)}
                isPrintPreview={isPrintPreview}
              />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_RIVE_ANIMATION && (
              <RivePrint {...(component as ComponentRiveAnimation)} />
            )}

            {component.type === ACTIVITIES_COMPONENT_TYPE_GIF && (
              <GifPrint {...(component as ComponentGif)} />
            )}

            {component.type ===
              ACTIVITIES_COMPONENT_SUB_ACTIVITY_TYPE_FILL_IN_BLANKS_RESPONSE && (
              <FillInBlanksPrint {...(component as ComponentFillInBlanks)} />
            )}
          </ComponentViewWrapper>
        );
      })}
    </ColumnPrintWrapper>
  );
};

export default ColumnsComponentsPrint;
